"use client";
import React, { useCallback, useEffect, useState } from "react";
import HRTitleHeader from "src/components/ui/HRTitleHeader";
import { Button } from "src/components/buttons/shadcn-button";
import OnboardingForm from "./components/OnboardingForm";
import OnboardingTable from "./components/OnboardingTable";
import { getNewHires } from "src/redux/api/_hr.api";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { setNewHire } from "src/redux/reducers";
import { useSearchParams } from "react-router-dom";
import { GenericPagination } from "src/components/ui/pagination";
import { toast } from "react-toastify";
import AppointmentContent from "./components/Appointment/AppointmentContent";

const RecruitmentOnboarding: React.FC = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const { newHire } = useAppSelector((state) => state.userReducer);

  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get("page") || "1", 10);
  const [failSafePageNumber, setFailSafePageNumber] = useState<number>();

  const fetchHires = useCallback(async () => {
    try {
      const response = await getNewHires(page);

      if (response.message.toLowerCase() === "success") {
        if (response.data.new_hire.length === 0) {
          if (failSafePageNumber) {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              page: failSafePageNumber.toString(),
            });
          }
          toast.info("No more Payrolls!");
          return;
        }

        if (
          Array.isArray(response.data.new_hire) &&
          response.data.new_hire.length > 0
        ) {
          dispatch(setNewHire(response.data.new_hire));
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, page, failSafePageNumber, searchParams, setSearchParams]);

  useEffect(() => {
    fetchHires();
  }, [fetchHires]);

  const handleNextPage = () => {
    setFailSafePageNumber(page);
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (page + 1).toString(),
    });
  };

  const handlePrevPage = () => {
    if (page === 1) {
      toast.info("This is the first page");
      return;
    }
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (page - 1).toString(),
    });
  };

  const handleJumpToPage = (page: number) => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: page.toString(),
    });
  };

  const [showForm, setShowForm] = useState(false);

  return (
    <div className="p-6 space-y-6">
      <HRTitleHeader
        title="Recruitment & Onboarding"
        info="Track openings, candidates and onboarding tasks"
      />

      {/* Add Hire Button */}
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "+ Add New Hire"}
        </Button>
      </div>

      {/* Form */}
      {showForm && <OnboardingForm refetch={fetchHires} />}

      {/* Table */}
      <div className="bg-card shadow-lg rounded-xl p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-800">New Hires</h3>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center text-gray-500 mt-10 flex items-center justify-center gap-2 w-full">
              <h2>Loading New Hires...</h2>
              <span className="loading loading-dots loading-md"></span>
            </div>
          ) : newHire.length === 0 ? (
            <p className="text-gray-500">No Hires Added Yet.</p>
          ) : (
            <div>
              <OnboardingTable hires={newHire} refetch={fetchHires} />
            </div>
          )}
        </div>
      </div>
      {newHire.length > 0 && (
        <div className="flex-grow my-3">
          <GenericPagination
            currentPage={page}
            onNext={handleNextPage}
            onPrev={handlePrevPage}
            jumpPage={handleJumpToPage}
          />
        </div>
      )}
      <AppointmentContent/>
    </div>
  );
};

export default RecruitmentOnboarding;
